import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Icon from '../../../src/components/Icon/Icon';
import VoiceTrackIcon from '../../../static/icons/ic_voicetrack_16.svg';
import { openMiniCoachmark } from '../../actions/MiniCoachmark';
import MiniCoachmark from '../../components/Overlay/MiniCoachmark';
import { MY_THUMBS_UP_PLAYLIST } from '../../constants/Catalog';
import { GridConstants } from '../../constants/Grid';
import { MiniCoachmarkTypes, MTUP_COACHMARK_CONFIG } from '../../constants/MiniCoachmark';
import { messages as playlistMessages } from '../../constants/PlaylistT3';
import { PandoraTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getArt, getDominantColor } from '../../lib/ArtUtils';
import { mapCuratorProps } from '../../lib/CuratorSourcesUtils';
import { getAnnotationsById } from '../../lib/EntityUtils';
import { getCurrentListenerIdFromState, getCurrentUserWebName } from '../../lib/ListenerUtils';
import { getPlaylistFromState } from '../../lib/MusicUtils';
import { isRadioOnly, isUnavailable } from '../../lib/RightsUtils';
import { getProfilePath } from '../../lib/RouteUtils';
import { getSourceCardMenuItems } from '../../lib/SourceCardUtils';

import CatalogGridItem from './CatalogGridItem';

const voiceTrackIcon = <Icon glyph={VoiceTrackIcon} />;

/**
 * PlaylistGridItem is for rendering T3 playlist grid items.
 *
 * See CatalogGridItem and lib/GridItem for additional available props.
 */

class PlaylistGridItem extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    pandoraType: PropTypes.string,
    isPlayingCurrently: PropTypes.bool,
    isPaused: PropTypes.bool,
    hidePlayButton: PropTypes.bool,
    sourceCardMenuItems: PropTypes.array,
    mainText: PropTypes.string,
    mainTextUrl: PropTypes.string,
    mainTextModifier: PropTypes.node,
    mtupPlaylistReadyForCoachmark: PropTypes.bool,
    openMiniCoachmark: PropTypes.func.isRequired,
    updateMtupPlaylistReadyForMinicoachmark: PropTypes.func.isRequired,
    itemOnclickUrl: PropTypes.string,
    author: PropTypes.string,
    playlistOwnerWebname: PropTypes.string,
    hideAuthor: PropTypes.bool,
    numSongs: PropTypes.number,
    intl: PropTypes.object,
    isHosted: PropTypes.bool,
    curatorName: PropTypes.string,
    curatorUrlPath: PropTypes.string,
  };

  componentDidMount() {
    if (this.props.mtupPlaylistReadyForCoachmark) {
      this.props.openMiniCoachmark(MiniCoachmarkTypes.MTUP_PLAYLIST);
    }
  }

  componentDidUpdate() {
    if (this.props.mtupPlaylistReadyForCoachmark) {
      // Some users have this component updated after first initial render and it may cause coachmark to disappear so keeping this extra check.
      this.props.openMiniCoachmark(MiniCoachmarkTypes.MTUP_PLAYLIST);
    }
  }

  componentWillUnmount() {
    if (this.props.mtupPlaylistReadyForCoachmark) {
      // Coachmark is already shown so set flag to false so we don't show it again.
      this.props.updateMtupPlaylistReadyForMinicoachmark(false);
    }
  }

  getSecondTextProps = () => {
    const { curatorName, curatorUrlPath, author, hideAuthor, playlistOwnerWebname } = this.props;

    if (curatorName && curatorUrlPath) {
      return {
        secondTextModifier: this.props.intl.formatMessage(playlistMessages.playlistBy, {
          author: '',
        }),
        secondText: curatorName,
        secondTextUrl: curatorUrlPath,
      };
    } else if (author && !hideAuthor) {
      // Don't show author if the author is the current user
      return {
        secondTextModifier: this.props.intl.formatMessage(playlistMessages.playlistBy, {
          author: '',
        }),
        secondText: author,
        secondTextUrl: getProfilePath(playlistOwnerWebname),
      };
    }

    return {
      secondTextModifier: this.props.intl.formatMessage(playlistMessages.playlist),
      secondText: '',
      secondTextUrl: null,
    };
  };

  render() {
    const secondTextProps = this.getSecondTextProps();

    const props = {
      ...secondTextProps,
      thirdText: this.props.intl.formatMessage(playlistMessages.songs, {
        numSongs: this.props.numSongs,
      }),
    };

    if (this.props.mtupPlaylistReadyForCoachmark) {
      return (
        <MiniCoachmark {...MTUP_COACHMARK_CONFIG}>
          <CatalogGridItem {...props} {...this.props} />
        </MiniCoachmark>
      );
    }

    return <CatalogGridItem {...props} {...this.props} />;
  }
}

export function mapStateToProps(state, ownProps) {
  const props = {};
  const id = ownProps.id;
  let details = getAnnotationsById(state, id);
  if (!details) {
    details = getPlaylistFromState(state, id);
  }

  props.blankItem = !details;
  if (details && id) {
    const { ownerName, ownerWebname } = getInDomain('playback.utils.getPlaylistOwnerInfo')(
      state,
      id,
      getCurrentListenerIdFromState(state),
    );
    const isEditable = getInDomain('playback.utils.isPlaylistEditable', false)(details);
    const backstageUrl = details.get('shareableUrlPath');
    const { curatorName, curatorUrlPath } = mapCuratorProps(state, id);

    props.mtupPlaylistReadyForCoachmark =
      details.get('linkedType') === MY_THUMBS_UP_PLAYLIST &&
      getInDomain('listener.selectors.mtupPlaylistReadyForMiniCoachmark')(state);
    props.sourceCardMenuItems = getSourceCardMenuItems({
      state,
      pandoraId: id,
      pandoraType: PandoraTypes.PLAYLIST,
      shareParentSource: {
        shareParentSourceId: id,
        shareParentSourceType: PandoraTypes.PLAYLIST,
      },
      playlistData: { isEditable },
    });
    props.isPlayingCurrently = getInDomain('playback.utils.isSourcePlayingCurrently', false)(
      state,
      id,
    );
    props.isPaused =
      !getInDomain('playback.utils.isAudioPlaying')(state) && props.isPlayingCurrently;
    props.mainText = details.get('name');
    props.mainTextUrl = backstageUrl;
    props.itemOnclickUrl = backstageUrl;
    props.playlistOwnerWebname = ownerWebname;
    props.author = ownerName || ownerWebname;
    props.hideAuthor = getCurrentUserWebName(state) === ownerWebname;
    props.numSongs = details.get('totalTracks');
    // If a playlist has zero tracks, don't show play button
    props.hidePlayButton = props.numSongs === 0;
    props.imgUrl = getArt(details, GridConstants.GRID_ITEM_IMAGE_SIZE, state);
    props.isRadioOnly = isRadioOnly(details);
    props.isUnavailable = isUnavailable(details);
    props.isExplicit = getInDomain('catalog.utils.isExplicit')(details);
    props.isClean = getInDomain('catalog.utils.isClean')(details);
    props.dominantColor = getDominantColor(details);
    props.isHosted = getInDomain('catalog.utils.isHostedPlaylist')(state, id);
    props.mainTextModifier = props.isHosted ? voiceTrackIcon : null;
    props.curatorName = curatorName;
    props.curatorUrlPath = curatorUrlPath;
  }
  return props;
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateMtupPlaylistReadyForMinicoachmark: getInDomain(
        'listener.actions.updateMtupPlaylistReadyForMinicoachmark',
      ),
      openMiniCoachmark,
    },
    dispatch,
  );
};

// We use injectIntl here because there was some inexplicable performance issues when using <FormattedMessage> directly in the props
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(PlaylistGridItem));
